import { useState, useMemo, useEffect } from 'react';
import { useRebootIncentive } from './useRebootIncentive';
import constants from '@playcastdotio/GlobalConfig';

interface ScheduleRestartProps {
  onSchedule: (epochSec: number) => void;
  onRestartNow: () => void;
  lastError?: string;
}

interface TimeOption {
  id: string;
  label: string;
  subtitle?: string;
  epochSec: number;
}

function toEpochSec(date: Date): number {
  return Math.floor(date.getTime() / 1000);
}

function computeTimeOptions(): TimeOption[] {
  const now = new Date();
  const options: TimeOption[] = [];

  // In 2 minutes (dev/testing only)
  if (constants.BRANCH !== 'release') {
    const twoMin = new Date(now.getTime() + 2 * 60 * 1000);
    options.push({
      id: '2min',
      label: 'In 2 minutes',
      subtitle: 'Quick test',
      epochSec: toEpochSec(twoMin),
    });
  }

  // Midnight option — label reflects whether the target is tonight or tomorrow
  const midnight = new Date(now);
  midnight.setDate(midnight.getDate() + 1);
  midnight.setHours(0, 0, 0, 0);
  if (midnight.getTime() - now.getTime() < 1000 * 60 * 60) {
    midnight.setDate(midnight.getDate() + 1);
  }
  const midnightIsTonight = midnight.getDate() === now.getDate() + 1
    || (midnight.getDate() === 1 && now.getDate() >= 28);
  const midnightLabel = midnightIsTonight ? 'Tonight' : 'Tomorrow night';
  options.push({
    id: 'midnight',
    label: `${midnightLabel} · 12:00 AM`,
    subtitle: 'Midnight · recommended',
    epochSec: toEpochSec(midnight),
  });

  // In 1 hour
  const oneHour = new Date(now.getTime() + 60 * 60 * 1000);
  options.push({
    id: '1h',
    label: 'In 1 hour',
    subtitle: 'Quick delay',
    epochSec: toEpochSec(oneHour),
  });

  // In 3 hours
  const threeHours = new Date(now.getTime() + 3 * 60 * 60 * 1000);
  options.push({
    id: '3h',
    label: 'In 3 hours',
    epochSec: toEpochSec(threeHours),
  });

  // 10pm option
  const tenPm = new Date(now);
  tenPm.setHours(22, 0, 0, 0);
  if (tenPm.getTime() <= now.getTime()) {
    tenPm.setDate(tenPm.getDate() + 1);
  }
  if (tenPm.getTime() - now.getTime() > 60 * 60 * 1000) {
    const isTomorrow = tenPm.getDate() !== now.getDate();
    options.push({
      id: '10pm',
      label: `${isTomorrow ? 'Tomorrow' : 'Tonight'} · 10:00 PM`,
      epochSec: toEpochSec(tenPm),
    });
  }

  // Tomorrow 8am
  const eightAm = new Date(now);
  eightAm.setDate(eightAm.getDate() + 1);
  eightAm.setHours(8, 0, 0, 0);
  options.push({
    id: '8am',
    label: 'Tomorrow · 8:00 AM',
    subtitle: 'Within 24 hours',
    epochSec: toEpochSec(eightAm),
  });

  return options;
}

export const ScheduleRestart = ({ onSchedule, onRestartNow, lastError }: ScheduleRestartProps) => {
  const options = useMemo(computeTimeOptions, []);
  const [selectedId, setSelectedId] = useState('midnight');
  const [customTime, setCustomTime] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const rebootLabel = useRebootIncentive();

  useEffect(() => {
    if (lastError) setSubmitting(false);
  }, [lastError]);

  const lockInDisabled = submitting || (showCustom && !customTime);

  const handleLockIn = () => {
    if (lockInDisabled) return;
    setSubmitting(true);
    if (showCustom && customTime) {
      const [hours, minutes] = customTime.split(':').map(Number);
      const customDate = new Date();
      customDate.setHours(hours, minutes, 0, 0);
      if (customDate.getTime() <= Date.now()) {
        customDate.setDate(customDate.getDate() + 1);
      }
      onSchedule(toEpochSec(customDate));
    } else {
      const selected = options.find((o) => o.id === selectedId);
      if (selected) {
        onSchedule(selected.epochSec);
      }
    }
  };

  const handleRestartNow = () => {
    if (submitting) return;
    setSubmitting(true);
    onRestartNow();
  };

  return (
    <div className='relative flex h-full w-full flex-col gap-3'>
      <p className='m-0 text-center text-[20px] font-[590] leading-[20px] text-[#e4e4e4]/92'>
        Schedule a restart
      </p>
      <p className='m-0 text-center text-[14px] font-normal leading-[20px] text-[#e4e4e4]/92'>
        Pick a time in the next 24 hours. Midnight is selected for the least friction.
      </p>

      <div className='flex-1 min-h-0 overflow-y-auto pr-1' style={{ scrollbarWidth: 'thin' }}>
        <div className='flex flex-col gap-2 w-full'>
          {options.map((option) => (
            <button
              key={option.id}
              type='button'
              onClick={() => { setSelectedId(option.id); setShowCustom(false); }}
              className='w-full text-left p-3 rounded-lg cursor-pointer'
              style={{
                border: `1px solid ${selectedId === option.id && !showCustom ? '#FBBF24' : 'rgba(228,228,228,0.12)'}`,
                background: selectedId === option.id && !showCustom ? 'rgba(228,228,228,0.118)' : 'rgba(228,228,228,0.067)',
              }}
            >
              <div className='flex flex-row items-center justify-between gap-2 w-full'>
                <div className='flex flex-col gap-0.5 w-full'>
                  <p className='m-0 text-[12px] font-[590] leading-[16px] text-[#e4e4e4]/92'>
                    {option.label}
                  </p>
                  {option.subtitle && (
                    <p className='m-0 text-[10px] font-normal leading-[16px] text-[#e4e4e4]/37'>
                      {option.subtitle}
                    </p>
                  )}
                </div>
                <div
                  className='w-[18px] h-[18px] rounded-full shrink-0'
                  style={{
                    border: `2px solid ${selectedId === option.id && !showCustom ? '#FBBF24' : 'rgba(228,228,228,0.12)'}`,
                    background: selectedId === option.id && !showCustom ? '#FBBF24' : 'transparent',
                  }}
                />
              </div>
            </button>
          ))}

          {/* Pick a time custom option */}
          <button
            type='button'
            onClick={() => setShowCustom(true)}
            className='w-full text-left p-3 rounded-lg cursor-pointer'
            style={{
              border: `1px solid ${showCustom ? '#FBBF24' : 'rgba(228,228,228,0.12)'}`,
              background: showCustom ? 'rgba(228,228,228,0.118)' : 'rgba(228,228,228,0.067)',
            }}
          >
            <div className='flex flex-row items-center justify-between gap-2 w-full'>
              <div className='flex flex-col gap-0.5 w-full'>
                <p className='m-0 text-[12px] font-[590] leading-[16px] text-[#e4e4e4]/92'>
                  Pick a time
                </p>
                <p className='m-0 text-[10px] font-normal leading-[16px] text-[#e4e4e4]/37'>
                  Any time in the next 24 hours
                </p>
              </div>
              <div
                className='w-[18px] h-[18px] rounded-full shrink-0'
                style={{
                  border: `2px solid ${showCustom ? '#FBBF24' : 'rgba(228,228,228,0.12)'}`,
                  background: showCustom ? '#FBBF24' : 'transparent',
                }}
              />
            </div>
          </button>

          {showCustom && (
            <div className='px-3 pb-1'>
              <input
                type='time'
                value={customTime}
                onChange={(e) => setCustomTime(e.target.value)}
                className='w-full rounded-lg border border-[#e4e4e4]/12 bg-[#e4e4e4]/7 px-3 py-2 text-sm text-white'
              />
            </div>
          )}
        </div>
      </div>

      <div className='flex flex-col gap-2 w-full shrink-0'>
        <button
          type='button'
          onClick={handleLockIn}
          disabled={lockInDisabled}
          className='flex w-full items-center justify-center rounded-full border border-[#FFFFFF]/15 bg-[#FFFFFF]/[0.067] text-[14px] font-medium text-white cursor-pointer transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed'
          style={{ height: 24, padding: '0 6px' }}
        >
          {submitting ? 'Scheduling...' : 'Lock in schedule'}
        </button>
        <button
          type='button'
          onClick={handleRestartNow}
          disabled={submitting}
          className='w-full min-h-[44px] rounded-full border-none bg-[#FBBF24] px-4 py-3 text-[15px] font-semibold text-[#141414] cursor-pointer transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed'
        >
          {submitting ? 'Restarting...' : rebootLabel}
        </button>
      </div>
    </div>
  );
};
